import {useMemo} from 'react'
import {Platform, View} from 'react-native'
import {Image} from 'expo-image'
import {AppBskyEmbedImages, type AppBskyFeedDefs} from '@atproto/api'
import {useQuery} from '@tanstack/react-query'

import {getPostVideo} from '#/lib/feed/mediaHelpers'
import {getSafeUiLabel} from '#/lib/strings/ui-labels'
import {tellusTvDebug} from '#/lib/tv/debug'
import {getMasterRecord, getThumbnailUrl} from '#/lib/tv/tvApi'
import {
  extractMasterUriFromVideoPage,
  extractTitleDescFromVideoPage,
  extractTotalDurationMsFromVideoPage,
} from '#/lib/tv/tvParser'
import {useAgent} from '#/state/session'
import {TimeElapsed} from '#/view/com/util/TimeElapsed'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {BadgeIcon} from '#/components/badges/BadgeIcon'
import {Play_Filled_Corner0_Rounded as PlayIcon} from '#/components/icons/Play'
import {Link} from '#/components/Link'
import {Text} from '#/components/Typography'

function formatDuration(totalMs?: number) {
  if (!totalMs || totalMs <= 0) return '--:--'
  const totalSec = Math.round(totalMs / 1000)
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = String(totalSec % 60).padStart(2, '0')
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
  }
  return `${minutes}:${seconds}`
}

export function TellusTVCard({
  post,
  progressRatio,
  width,
  variant = 'grid',
}: {
  post: AppBskyFeedDefs.PostView
  progressRatio?: number
  width?: number | string
  variant?: 'grid' | 'compact'
}) {
  const t = useTheme()
  const agent = useAgent()
  const resolvedWidth =
    typeof width === 'number' ? width : variant === 'grid' ? 320 : 168
  const fallbackThumbHeight = Math.round((resolvedWidth * 9) / 16)
  const {title} = useMemo(() => extractTitleDescFromVideoPage(post), [post])
  const masterUri = useMemo(() => extractMasterUriFromVideoPage(post), [post])
  const totalFromText = useMemo(
    () => extractTotalDurationMsFromVideoPage(post),
    [post],
  )

  const {data: master} = useQuery({
    queryKey: ['tellustv-master', masterUri],
    queryFn: () => getMasterRecord(agent, masterUri || ''),
    enabled: !!masterUri,
  })

  const masterThumbnail = useMemo(() => {
    if (!master || !masterUri) return undefined
    return getThumbnailUrl(agent, masterUri, master)
  }, [agent, master, masterUri])

  const thumbnail = useMemo(() => {
    if (masterThumbnail) return masterThumbnail
    if (AppBskyEmbedImages.isView(post.embed)) {
      return (
        post.embed.images[0]?.thumb ||
        post.embed.images[0]?.fullsize ||
        undefined
      )
    }
    const video = getPostVideo(post)
    return video?.thumb
  }, [masterThumbnail, post])

  const durationLabel = formatDuration(master?.totalDurationMs || totalFromText)
  const uriEncoded = encodeURIComponent(post.uri)
  const authorName = post.author.displayName || post.author.handle
  const safeTitle = getSafeUiLabel(title, {
    context: 'tellustv-card-title',
    fallback: '—',
  })

  const clampStyle = {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  } as const
  const gridTitleStyle = [a.text_sm, a.font_bold, a.leading_snug, clampStyle]
  const compactTitleStyle = [
    a.text_sm,
    a.font_bold,
    {lineHeight: 18},
    clampStyle,
  ]

  if (variant === 'compact') {
    return (
      <Link
        to={{screen: 'TellusTVWatch', params: {uriEncoded}}}
        label={safeTitle}
        onPress={() => {
          tellusTvDebug('thumbnail click', {
            variant,
            postUri: post.uri,
            uriEncoded,
          })
        }}
        style={[a.flex_row, a.gap_md, {width: width ?? 360}]}>
        <View
          style={[
            a.relative,
            a.overflow_hidden,
            {
              backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
              borderRadius: 8,
            },
          ]}>
          {thumbnail ? (
            <Image
              source={{uri: thumbnail}}
              style={[{width: 168, aspectRatio: 16 / 9, borderRadius: 8}]}
              accessibilityIgnoresInvertColors
            />
          ) : (
            <View
              style={[
                a.align_center,
                a.justify_center,
                {
                  width: 168,
                  aspectRatio: 16 / 9,
                  backgroundColor: '#111',
                  borderRadius: 8,
                },
              ]}>
              <PlayIcon width={20} height={20} fill="white" />
            </View>
          )}
          <View
            style={[
              a.absolute,
              a.right_xs,
              a.bottom_xs,
              a.rounded_sm,
              a.px_xs,
              a.py_2xs,
              {backgroundColor: 'rgba(0,0,0,0.7)'},
            ]}>
            <Text style={[a.text_xs, {color: 'white'}]}>{durationLabel}</Text>
          </View>
        </View>
        <View style={[a.flex_1, {gap: 4}]}>
          <Text style={compactTitleStyle} numberOfLines={2} emoji>
            {safeTitle}
          </Text>
          <View style={[a.flex_row, a.align_center, a.gap_xs]}>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
              {authorName}
            </Text>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>•</Text>
            <TimeElapsed timestamp={post.indexedAt}>
              {({timeElapsed}) => (
                <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                  {timeElapsed}
                </Text>
              )}
            </TimeElapsed>
          </View>
        </View>
      </Link>
    )
  }

  const isWeb = Platform.OS === 'web'

  return (
    <Link
      to={{screen: 'TellusTVWatch', params: {uriEncoded}}}
      label={safeTitle}
      onPress={() => {
        tellusTvDebug('thumbnail click', {
          variant,
          postUri: post.uri,
          uriEncoded,
        })
      }}
      style={[
        a.flex_col,
        a.align_stretch,
        a.gap_sm,
        {width: width ?? 280, flexShrink: 0},
      ]}>
      <View
        style={[
          a.relative,
          a.overflow_hidden,
          a.rounded_md,
          {
            backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
            width: '100%',
          },
        ]}>
        {isWeb ? (
          <>
            <View style={[{width: '100%', paddingTop: '56.25%'}]} />
            {thumbnail ? (
              <Image
                source={{uri: thumbnail}}
                style={[a.absolute, {inset: 0, width: '100%', height: '100%'}]}
                contentFit="cover"
                accessibilityIgnoresInvertColors
              />
            ) : (
              <View
                style={[
                  a.absolute,
                  a.inset_0,
                  a.align_center,
                  a.justify_center,
                  {backgroundColor: '#111'},
                ]}>
                <PlayIcon width={28} height={28} fill="white" />
              </View>
            )}
          </>
        ) : (
          <>
            {thumbnail ? (
              <Image
                source={{uri: thumbnail}}
                style={[{width: '100%', height: fallbackThumbHeight}]}
                contentFit="cover"
                accessibilityIgnoresInvertColors
              />
            ) : (
              <View
                style={[
                  {width: '100%', height: fallbackThumbHeight},
                  a.align_center,
                  a.justify_center,
                  {backgroundColor: '#111'},
                ]}>
                <PlayIcon width={28} height={28} fill="white" />
              </View>
            )}
          </>
        )}
        <View
          style={[
            a.absolute,
            a.right_xs,
            a.bottom_xs,
            a.rounded_sm,
            a.px_xs,
            a.py_2xs,
            {backgroundColor: 'rgba(0,0,0,0.7)'},
          ]}>
          <Text style={[a.text_xs, {color: 'white'}]}>{durationLabel}</Text>
        </View>
        {progressRatio !== undefined ? (
          <View style={[a.absolute, a.left_0, a.right_0, {bottom: 0}]}>
            <View
              style={[{height: 3, backgroundColor: 'rgba(255,255,255,0.2)'}]}
            />
            <View
              style={[
                a.absolute,
                {
                  height: 3,
                  width: `${Math.max(0, Math.min(1, progressRatio)) * 100}%`,
                  backgroundColor: t.palette.primary_500,
                },
              ]}
            />
          </View>
        ) : null}
      </View>
      <View style={[a.flex_row, a.gap_sm]}>
        <UserAvatar type="user" size={36} avatar={post.author.avatar} />
        <View style={[a.flex_1]}>
          <Text style={gridTitleStyle} numberOfLines={2} emoji>
            {safeTitle}
          </Text>
          <View style={[a.flex_row, a.align_center, a.gap_xs]}>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
              {authorName}
            </Text>
            <BadgeIcon actor={post.author} size={10} />
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>•</Text>
            <TimeElapsed timestamp={post.indexedAt}>
              {({timeElapsed}) => (
                <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                  {timeElapsed}
                </Text>
              )}
            </TimeElapsed>
          </View>
        </View>
      </View>
    </Link>
  )
}
